import { reactive, readonly } from 'vue';
import { lineServiceState, sortedStateEvents } from '@/utils/orderPaymentState';

// Valoración del restaurante en Google para el cliente del QR.
//
// Cuándo se ofrece (al terminar la experiencia, nunca mientras pide):
// - Paga después de comer: al pagar su parte o la cuenta completa
//   ('bill-paid') o al pedir la cuenta al camarero ('bill-requested').
// - Paga antes de comer (barra, take away, delivery, pago al pedir): cuando
//   todo lo pedido está entregado y ha pasado un rato ('order-finished').
// - Al cerrarse la mesa, en la pantalla de agradecimiento (sin ventana).
//
// Frecuencia: una vez por visita (sesión de mesa) y por dispositivo. "Ahora
// no" lo aparca 30 días; si pulsa "Valorar en Google" no se vuelve a pedir en
// un año. Se ofrece a todos por igual: Google no permite pedir reseñas solo a
// los clientes contentos ni dar nada a cambio.

const STORE_KEY = 'ewu_google_review_v1';
const CONTEXT_KEY = 'ewu_google_review_context_v1';
const DAY_MS = 24 * 60 * 60 * 1000;
const DISMISS_SNOOZE_MS = 30 * DAY_MS;
const CLICKED_SNOOZE_MS = 365 * DAY_MS;
const CLOSED_VISIT_MAX_AGE_MS = 6 * 60 * 60 * 1000;
const MAX_STORED_RESTAURANTS = 20;

export const REVIEW_AFTER_DELIVERY_MS = 20 * 60 * 1000;
const STALE_DELIVERY_MS = 12 * 60 * 60 * 1000;

const MOMENT_FLOW = {
  'bill-paid': 'pay-after',
  'bill-requested': 'pay-after',
  'order-finished': 'pay-before',
};

const state = reactive({
  visible: false,
  url: null,
  restaurantName: '',
  moment: null,
});

let config = { url: null, restaurantName: '', payBeforeEating: false, visitKey: '' };
let pendingTimer = null;

function readJson(key, fallback) {
  try {
    const value = JSON.parse(localStorage.getItem(key) || 'null');
    return value && typeof value === 'object' ? value : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  try {
    if (value == null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Sin almacenamiento (modo privado...): se ofrece igual, solo que sin memoria.
  }
}

function entryFor(url) {
  return readJson(STORE_KEY, {})[url] || {};
}

function updateEntry(url, patch) {
  if (!url) return;
  const store = readJson(STORE_KEY, {});
  store[url] = { ...(store[url] || {}), ...patch, updatedAt: Date.now() };
  const urls = Object.keys(store);
  if (urls.length > MAX_STORED_RESTAURANTS) {
    urls
      .sort((a, b) => (store[a].updatedAt || 0) - (store[b].updatedAt || 0))
      .slice(0, urls.length - MAX_STORED_RESTAURANTS)
      .forEach((oldUrl) => delete store[oldUrl]);
  }
  writeJson(STORE_KEY, store);
}

// No guardamos el token de la mesa, solo una huella para reconocer la visita.
function visitFingerprint(value) {
  let hash = 0x811c9dc5;
  const text = String(value || '');
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
}

function recentlyClicked(entry, now = Date.now()) {
  return Boolean(entry.clickedAt && now - entry.clickedAt < CLICKED_SNOOZE_MS);
}

/** App.vue lo llama con los datos de la sesión de mesa (me_table). */
export function configureGoogleReview({ url, restaurantName = '', payBeforeEating = false, visitKey = '' } = {}) {
  const reviewUrl = typeof url === 'string' && url.trim() ? url.trim() : null;
  config = {
    url: reviewUrl,
    restaurantName: String(restaurantName || ''),
    payBeforeEating: Boolean(payBeforeEating),
    visitKey: visitKey ? visitFingerprint(visitKey) : '',
  };
  if (reviewUrl) {
    writeJson(CONTEXT_KEY, { url: reviewUrl, restaurantName: config.restaurantName, at: Date.now() });
  } else {
    writeJson(CONTEXT_KEY, null);
    cancelPendingOffer();
    state.visible = false;
  }
}

export function canOfferGoogleReview(moment, now = Date.now()) {
  const flow = MOMENT_FLOW[moment];
  if (!config.url || !flow) return false;
  if ((flow === 'pay-before') !== config.payBeforeEating) return false;
  const entry = entryFor(config.url);
  if (recentlyClicked(entry, now)) return false;
  if (entry.dismissedAt && now - entry.dismissedAt < DISMISS_SNOOZE_MS) return false;
  if (config.visitKey && entry.offeredVisit === config.visitKey) return false;
  return true;
}

function cancelPendingOffer() {
  if (pendingTimer) clearTimeout(pendingTimer);
  pendingTimer = null;
}

/** Pide mostrar la ventana en un momento dado (se ignora si no toca). */
export function offerGoogleReview(moment, { delayMs = 0 } = {}) {
  if (state.visible || pendingTimer || !canOfferGoogleReview(moment)) return false;
  pendingTimer = setTimeout(() => {
    pendingTimer = null;
    if (state.visible || !canOfferGoogleReview(moment)) return;
    updateEntry(config.url, { offeredVisit: config.visitKey, offeredAt: Date.now() });
    state.url = config.url;
    state.restaurantName = config.restaurantName;
    state.moment = moment;
    state.visible = true;
  }, Math.max(0, Number(delayMs) || 0));
  return true;
}

/** El cliente pulsa "Valorar en Google" (el enlace se abre en otra pestaña). */
export function markGoogleReviewClicked(url = state.url) {
  updateEntry(url, { clickedAt: Date.now() });
  state.visible = false;
}

/** "Ahora no" o cerrar la ventana. */
export function dismissGoogleReview() {
  updateEntry(state.url, { dismissedAt: Date.now() });
  state.visible = false;
}

/**
 * Para la pantalla de "mesa cerrada": el enlace de la visita que acaba de
 * terminar en este dispositivo, si no ha valorado ya.
 */
export function closedVisitGoogleReview(now = Date.now()) {
  const context = readJson(CONTEXT_KEY, null);
  if (!context?.url || !context.at || now - context.at > CLOSED_VISIT_MAX_AGE_MS) return null;
  if (recentlyClicked(entryFor(context.url), now)) return null;
  return { url: context.url, restaurantName: context.restaurantName || '' };
}

const FINISHED_STATES = new Set(['ENTREGADO']);
const CANCELLED_STATES = new Set(['CANCELADO', 'CANCELADO POR CLIENTE']);

function finishedEventTime(line, finishedStates) {
  const event = sortedStateEvents(line).find((item) => {
    const name = String(item?.state?.name || item?.state || '').trim().toUpperCase();
    return finishedStates.has(name);
  });
  const time = Date.parse(event?.created_at || '');
  return Number.isFinite(time) ? time : null;
}

/**
 * Momento en que el cliente terminó de recibir todo lo pedido (pedidos de
 * "Mis pedidos"), o null si aún queda algo por llegar. Si no hay hora fiable
 * devuelve `fallbackTime`. En take away "Listo" ya cuenta como recogido.
 */
export function allOrdersFinishedAt(orders = [], fallbackTime = null) {
  let latest = null;
  let finishedLines = 0;
  for (const order of Array.isArray(orders) ? orders : []) {
    const finishedStates = (order?.take_aways || []).length
      ? new Set([...FINISHED_STATES, 'LISTO'])
      : FINISHED_STATES;
    for (const line of order?.shopping_carts || []) {
      const lineState = lineServiceState(line, 'PENDIENTE');
      if (CANCELLED_STATES.has(lineState)) continue;
      if (!finishedStates.has(lineState)) return null;
      finishedLines += 1;
      const time = finishedEventTime(line, finishedStates);
      if (time != null && (latest == null || time > latest)) latest = time;
    }
  }
  if (!finishedLines) return null;
  return latest ?? fallbackTime;
}

/** "Mis pedidos" lo llama en cada actualización. */
export function offerGoogleReviewAfterMeal(orders, { fallbackTime = null, now = Date.now() } = {}) {
  const finishedAt = allOrdersFinishedAt(orders, fallbackTime);
  if (finishedAt == null) return false;
  const elapsed = now - finishedAt;
  if (elapsed < REVIEW_AFTER_DELIVERY_MS || elapsed > STALE_DELIVERY_MS) return false;
  return offerGoogleReview('order-finished', { delayMs: 800 });
}

export const googleReviewState = readonly(state);
